'use client';

import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Zap } from 'lucide-react';

const chartData = [
  { date: 'Jan 6', impressions: 2980, clicks: 820, isSpike: false },
  { date: 'Jan 9', impressions: 3120, clicks: 890, isSpike: false },
  { date: 'Jan 12', impressions: 2870, clicks: 780, isSpike: false },
  { date: 'Jan 15', impressions: 3340, clicks: 950, isSpike: false },
  { date: 'Jan 18', impressions: 3080, clicks: 860, isSpike: false },
  { date: 'Jan 21', impressions: 2990, clicks: 830, isSpike: false },
  { date: 'Jan 24', impressions: 3200, clicks: 900, isSpike: false },
  { date: 'Jan 27', impressions: 5840, clicks: 1920, isSpike: true },
  { date: 'Jan 30', impressions: 3410, clicks: 980, isSpike: false },
  { date: 'Feb 2', impressions: 3180, clicks: 890, isSpike: false },
  { date: 'Feb 5', impressions: 3290, clicks: 920, isSpike: false },
  { date: 'Feb 8', impressions: 3050, clicks: 840, isSpike: false },
  { date: 'Feb 11', impressions: 3380, clicks: 960, isSpike: false },
  { date: 'Feb 14', impressions: 3540, clicks: 1020, isSpike: false },
  { date: 'Feb 17', impressions: 3210, clicks: 910, isSpike: false },
  { date: 'Feb 20', impressions: 3090, clicks: 860, isSpike: false },
  { date: 'Feb 23', impressions: 3440, clicks: 980, isSpike: false },
  { date: 'Feb 26', impressions: 6120, clicks: 2140, isSpike: true },
  { date: 'Mar 1', impressions: 4200, clicks: 1340, isSpike: false },
  { date: 'Mar 4', impressions: 3680, clicks: 1080, isSpike: false },
  { date: 'Mar 7', impressions: 3520, clicks: 1010, isSpike: false },
  { date: 'Mar 10', impressions: 3390, clicks: 970, isSpike: false },
  { date: 'Mar 13', impressions: 3610, clicks: 1050, isSpike: false },
  { date: 'Mar 16', impressions: 3480, clicks: 1000, isSpike: false },
  { date: 'Mar 19', impressions: 3720, clicks: 1090, isSpike: false },
  { date: 'Mar 22', impressions: 3560, clicks: 1030, isSpike: false },
  { date: 'Mar 25', impressions: 3840, clicks: 1140, isSpike: false },
  { date: 'Mar 28', impressions: 6940, clicks: 2480, isSpike: true },
  { date: 'Mar 31', impressions: 4580, clicks: 1560, isSpike: false },
  { date: 'Apr 3', impressions: 4120, clicks: 1380, isSpike: false },
  { date: 'Apr 6', impressions: 5240, clicks: 1820, isSpike: false },
];

const spikeBaseline = 3900;

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const isSpike = (payload[0]?.value ?? 0) > spikeBaseline;
  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.12)_0px_8px_24px] p-3 min-w-[160px]">
      <p className="text-xs font-600 text-[#39364E] mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={`tooltip-${entry.name}`} className="flex items-center justify-between gap-4 text-xs mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-[#696969] capitalize">{entry.name}</span>
          </div>
          <span className="font-600 tabular-nums text-[#39364E]">{entry.value.toLocaleString()}</span>
        </div>
      ))}
      {isSpike && (
        <div className="mt-2 pt-2 border-t border-[#e7e7e7] flex items-center gap-1">
          <Zap size={11} className="text-[#961932]" />
          <span className="text-[10px] font-600 text-[#961932]">Spike detected</span>
        </div>
      )}
    </div>
  );
};

export default function BrandedTrendChart() {
  const [showClicks, setShowClicks] = useState(true);

  const spikeDates = chartData.filter((d) => d.isSpike).map((d) => d.date);

  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] p-5 hover:shadow-[rgba(150,25,50,0.08)_0px_8px_24px] transition-shadow duration-200">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Branded Search Trend</h2>
          <p className="text-xs text-[#696969] mt-0.5">Daily impressions & clicks · Last 90 days · acme.io</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <div
              onClick={() => setShowClicks(!showClicks)}
              className={`w-8 h-4 rounded-full transition-colors duration-200 relative ${
                showClicks ? 'bg-[#961932]' : 'bg-[#e7e7e7]'
              }`}
            >
              <div
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${
                  showClicks ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </div>
            <span className="text-xs text-[#696969]">Clicks</span>
          </label>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-[#961932]" />
          <span className="text-[11px] text-[#696969]">Impressions</span>
        </div>
        {showClicks && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-[#8C0B31]" />
            <span className="text-[11px] text-[#696969]">Clicks</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 border-t-2 border-dashed border-[rgba(150,25,50,0.4)]" />
          <span className="text-[11px] text-[#696969]">Spike threshold</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Zap size={11} className="text-[#961932]" />
          <span className="text-[11px] text-[#696969]">Spike events (3)</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="trendImpressionsGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#961932" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#961932" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="trendClicksGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8C0B31" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#8C0B31" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#f0e8ea" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#696969' }}
            axisLine={false}
            tickLine={false}
            interval={4}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#696969' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)
            }
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={spikeBaseline}
            stroke="rgba(150,25,50,0.4)"
            strokeDasharray="5 3"
            strokeWidth={1.5}
            label={{
              value: 'Threshold',
              position: 'insideTopRight',
              fontSize: 10,
              fill: '#961932',
            }}
          />
          {spikeDates.map((date) => (
            <ReferenceLine
              key={`spike-ref-${date}`}
              x={date}
              stroke="rgba(150,25,50,0.3)"
              strokeWidth={1}
            />
          ))}
          <Area
            type="monotone"
            dataKey="impressions"
            stroke="#961932"
            strokeWidth={2}
            fill="url(#trendImpressionsGrad)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: '#961932' }}
          />
          {showClicks && (
            <Area
              type="monotone"
              dataKey="clicks"
              stroke="#8C0B31"
              strokeWidth={2}
              fill="url(#trendClicksGrad)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: '#8C0B31' }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}